import { AgGridAngular } from "@ag-grid-community/angular";
import {
  ColDef,
  FirstDataRenderedEvent,
  GetContextMenuItemsParams,
  GridOptions,
  ICellRendererParams,
  IRowNode,
  ValueFormatterParams,
  ValueGetterParams,
} from "@ag-grid-community/core";

import { AsyncPipe, CurrencyPipe } from "@angular/common";
import { ChangeDetectionStrategy, Component, computed, OnInit, signal, ViewChild, inject } from "@angular/core";
import { toSignal } from "@angular/core/rxjs-interop";
import { ActivatedRoute, ParamMap, Router } from "@angular/router";
import {
  AccountMarketplace,
  AccountState,
  Currency,
  Product,
  ProductTrackerConfig,
  SearchTermAsinRank,
  User,
} from "@front/m19-api-client";
import { gridConfig } from "@front/m19-grid-config";
import { Marketplaces, SegmentConfigType, SegmentEx } from "@front/m19-models";
import {
  AccountSelectionService,
  AsinService,
  AuthService,
  KeywordTrackingService,
  SegmentService,
} from "@front/m19-services";
import {
  ActionButton,
  ActionButtonsComponent,
  IButtonDirective,
  IIconComponent,
  ISelectMenuImports,
  Option,
} from "@front/m19-ui";
import { TranslocoDirective, TranslocoService } from "@jsverse/transloco";
import { ProductThumbnailGridComponent } from "@m19-board/product-view/product-thumbnail-grid.component";
import { LoadingService } from "@m19-board/services/loading.service";
import { ExportButtonComponent } from "@m19-board/shared/ui/export-buttons/export-button.component";
import { keywordRankingAvailableFor } from "@m19-board/tracking/KeywordRankingAvailability";
import { UntilDestroy, untilDestroyed } from "@ngneat/until-destroy";
import { combineLatest, map, Observable, switchMap, tap } from "rxjs";
import { actionColumnProperties, ImageColumn } from "../../grid-config/grid-columns";
import { ACTIONS_COL_ID, exportGridCsv, getCsvFileName, IMAGE_COL_ID } from "../../grid-config/grid-config";
import { AsinLinkComponent } from "../../product-view/asin-link.component";
import { KeywordTrackerService } from "../../services/keyword-tracker.service";
import { ProductTrackerService } from "../../services/product-tracker.service";
import { ReviewScoreComponent } from "../../tracking/review-score/review-score.component";
import { DisplayGroup, DisplayGroupsMap } from "../../utils/displayGroups";

export enum ProductTrackerTableColumns {
  Brand = "brand",
  MainCategory = "mainCategory",
  MainCategoryBSR = "mainCategoryBSR",
  SubCategory = "subCategory",
  SubCategoryBSR = "subCategoryBSR",
  Price = "price",
  Reviews = "reviews",
  Actions = "actions",
}

enum CatalogFilter {
  All = "All Products",
  Catalog = "Only from Catalog",
  NotInCatalog = "Not in Catalog",
}

export type ProductTrackerRow = Product & {
  trackedSearchTerms: string[];
  inCatalog: boolean;
  parentAsin?: string;
};

export interface ProductMetric<T> {
  title: string;
  field: string;
  tooltip: string;
  value: (product: Product) => T;
  formattedValue: (product: Product) => string;
  numeric?: boolean;
}

function productMetric<K extends keyof Product>(
  field: K,
  title: string,
  tooltip?: string,
  numeric?: boolean,
): ProductMetric<Product[K]> {
  return {
    title,
    field,
    tooltip: tooltip ?? "",
    value: (p) => p[field],
    formattedValue: (p) => p[field]?.toString() ?? "",
    numeric,
  };
}

export const SimpleProductMetrics = {
  [ProductTrackerTableColumns.Brand]: productMetric("brand", "common.brand"),
  [ProductTrackerTableColumns.MainCategory]: productMetric("displayGroup", "product-tracker.main_category"),
  [ProductTrackerTableColumns.MainCategoryBSR]: productMetric(
    "displayGroupRank",
    "product-tracker.main_category_bsr",
    "Main Category Best Seller Rank",
    true,
  ),
  [ProductTrackerTableColumns.SubCategory]: productMetric("categoryId", "product-tracker.sub_category"),
  [ProductTrackerTableColumns.SubCategoryBSR]: productMetric(
    "categoryRank",
    "product-tracker.sub_category_bsr",
    "Sub Category Best Seller Rank",
    true,
  ),
  [ProductTrackerTableColumns.Reviews]: productMetric("reviews", "product-tracker.number_of_reviews", undefined, true),
};

export enum ProductTrackerPageQueryParams {
  segment = "segment",
  asin = "trackedAsin",
}

export type TrackedSearchTerm = {
  searchTerm: string;
  timeline: SearchTermAsinRank[];
};

@UntilDestroy()
@Component({
  selector: "app-product-tracker",
  templateUrl: "./product-tracker.component.html",
  styleUrls: ["./product-tracker.component.scss"],
  imports: [
    TranslocoDirective,
    IButtonDirective,
    AsyncPipe,
    ExportButtonComponent,
    AgGridAngular,
    IIconComponent,
    ISelectMenuImports,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ProductTrackerComponent implements OnInit {
  private accountSelection = inject(AccountSelectionService);
  private authService = inject(AuthService);
  private asinService = inject(AsinService);
  private route = inject(ActivatedRoute);
  private router = inject(Router);
  private segmentService = inject(SegmentService);
  private keywordTrackerService = inject(KeywordTrackerService);
  private loadingService = inject(LoadingService);
  private productTrackerService = inject(ProductTrackerService);
  private keywordTrackingService = inject(KeywordTrackingService);
  private translocoService = inject(TranslocoService);

  readonly catalogFilterDdOptions: Option<CatalogFilter>[] = [
    {
      label: this.translocoService.translate("product-tracker.all_products"),
      value: CatalogFilter.All,
    },
    {
      label: this.translocoService.translate("product-tracker.only_from_catalog"),
      value: CatalogFilter.Catalog,
    },
    {
      label: this.translocoService.translate("product-tracker.not_in_catalog"),
      value: CatalogFilter.NotInCatalog,
    },
  ];
  catalogFilterSelected = signal<Option<CatalogFilter>>(this.catalogFilterDdOptions[0]);

  readonly PRODUCT_TRACKER_GRID_KEY = "productTrackerGrid";

  data?: ProductTrackerRow[];
  dataByParentAsin = new Map<string, ProductTrackerRow>();

  currency?: Currency;
  locale?: string;
  accountMarketplace?: AccountMarketplace;
  readonly keywordTrackingSupported = signal(false);
  filter = "";

  private readonly segments$ = this.accountSelection.singleAccountMarketplaceSelection$.pipe(
    switchMap((am) => this.segmentService.getSegments(am.accountId, am.marketplace)),
    map((segments) => Array.from(segments.values()).filter((s) => s.segmentType === SegmentConfigType.ProductSegment)),
  );
  private readonly segments = toSignal(this.segments$);
  readonly filteredSegments = computed(
    () => this.segments()?.filter((s) => s.name.toLowerCase().includes(this.segmentQuery().toLowerCase())) ?? [],
  );
  selectedSegments = signal<SegmentEx[]>([]);
  segmentQuery = signal<string>("");

  productTrackerConfig: ProductTrackerConfig[] = [];
  asinInitValue?: string;

  isReadOnly$: Observable<boolean> = this.accountSelection.readOnlyMode$;
  readonly isGroupedByParent = signal(false);
  readonly productTrackerUrl = "/product-center/product-tracker";

  @ViewChild(AgGridAngular) agGrid!: AgGridAngular;

  private colDefs: ColDef[] = [
    {
      ...ImageColumn,
      headerName: this.translocoService.translate("catalog-page.image", {}, "en"),
      headerValueGetter: () => this.translocoService.translate("catalog-page.image"),
      pinned: undefined,
      cellRendererSelector: (params) => {
        const groupByParent = params.node?.field === "parentAsin";
        const asin = groupByParent ? params.node.key : params.value;
        if (!asin || asin === "") return undefined;
        return {
          component: ProductThumbnailGridComponent,
          params: {
            asin: asin,
            marketplace: this.accountMarketplace?.marketplace,
            smallImg: true,
          },
        };
      },
    },
    {
      field: "asin",
      headerName: this.translocoService.translate("common.asin", {}, "en"),
      headerValueGetter: () => this.translocoService.translate("common.asin"),
      filter: "agTextColumnFilter",
      cellRendererSelector: (params: ICellRendererParams<ProductTrackerRow>) => {
        if (!params.node.group) {
          return {
            component: AsinLinkComponent,
            params: { asin: params.data?.asin, marketplace: this.accountMarketplace?.marketplace },
          };
        }
        return undefined;
      },
    },
    {
      headerName: this.translocoService.translate("product360.parent_asin", {}, "en"),
      headerValueGetter: () => this.translocoService.translate("product360.parent_asin"),
      field: "parentAsin",
      hide: true,
      enableRowGroup: true,
    },
    {
      headerName: this.translocoService.translate("common.title", {}, "en"),
      headerValueGetter: () => this.translocoService.translate("common.title"),
      field: "title",
      filter: "agTextColumnFilter",
      tooltipField: "title",
      width: 400,
      valueFormatter: (params: ValueFormatterParams<ProductTrackerRow>) => {
        if (params.node?.field === "parentAsin") return this.dataByParentAsin.get(params.node.key!)?.title;
        return params.value;
      },
    },
    {
      headerName: this.translocoService.translate("product-tracker.number_of_tracked_keywords", {}, "en"),
      headerValueGetter: () => this.translocoService.translate("product-tracker.number_of_tracked_keywords"),
      field: "trackedSearchTerms",
      colId: "trackedSearchTerms",
      valueGetter: (params: ValueGetterParams<ProductTrackerRow>) => {
        if (params.node?.field === "parentAsin")
          return this.dataByParentAsin.get(params.node.key!)?.trackedSearchTerms.length;
        return params.data?.trackedSearchTerms.length;
      },
      type: "numericColumn",
      filter: "agNumberColumnFilter",
    },
    {
      headerName: this.translocoService.translate("product-tracker.main_category", {}, "en"),
      headerValueGetter: () => this.translocoService.translate("product-tracker.main_category"),
      field: "displayGroup",
      filterParams: {
        valueFormatter: (params: ValueFormatterParams) => this.displayGroupFormatter(params, false),
      },
      cellRenderer: (params: ICellRendererParams) => this.displayGroupFormatter(params, true),
    },
    ...(this.getProductMetricsDefs() as any),
    {
      field: "price",
      headerName: this.translocoService.translate("common.price", {}, "en"),
      headerValueGetter: () => this.translocoService.translate("common.price"),
      valueFormatter: (params: ValueFormatterParams<ProductTrackerRow>) => {
        const val = params.node?.group ? params.node.aggData.price : params.value;
        return new CurrencyPipe(this.locale!).transform(val, this.currency, "symbol", "1.2-2");
      },
      type: "numericColumn",
      filter: "agNumberColumnFilter",
    },
    {
      field: "reviewScore",
      headerName: this.translocoService.translate("product-tracker.review_score", {}, "en"),
      headerValueGetter: () => this.translocoService.translate("product-tracker.review_score"),
      cellRenderer: ReviewScoreComponent,
      type: "numericColumn",
      filter: "agNumberColumnFilter",
      tooltipField: "reviewScore",
    },
    {
      ...actionColumnProperties<ProductTrackerRow, string>(),
      cellRendererSelector: (params) => {
        if (params.node.group && params.node.field !== "parentAsin") return undefined;
        if (params.node.group && (params.node.key === undefined || params.node.key === "")) return undefined;
        const buttons: ActionButton[] = [];
        if (this.keywordTrackingSupported()) {
          buttons.push({
            icon: "list",
            tooltip: this.translocoService.translate("product-tracker.tracked_keywords_details"),
            onClick: (params: ICellRendererParams | GetContextMenuItemsParams) => {
              this.navigateToTrackedKeywordsDetails(params.node?.group ? params.node.key : params.node?.data.asin);
            },
          });
        }

        if (!params.node.group) {
          buttons.push({
            icon: "chartLine",
            tooltip: this.translocoService.translate("product-tracker.product_timeline_details"),
            onClick: (params: ICellRendererParams | GetContextMenuItemsParams) => {
              this.navigateToProductTimeline(params.node?.data.asin);
            },
          });
        }

        return {
          component: ActionButtonsComponent,
          params: {
            actionButtons: buttons,
          },
        };
      },
    },
  ];

  basicOpts: GridOptions = gridConfig({ gridKey: this.PRODUCT_TRACKER_GRID_KEY });
  gridOptions: GridOptions = {
    ...this.basicOpts,
    context: { componentParent: this },
    columnDefs: this.colDefs,
    defaultColDef: {
      sortable: true,
      filter: true,
      resizable: true,
      floatingFilter: true,
    },
    showOpenedGroup: false,
    rowGroupPanelShow: "always",
    enableRangeSelection: false,
    pagination: true,
    onFirstDataRendered: (params: FirstDataRenderedEvent) => {
      if (this.basicOpts.onFirstDataRendered) this.basicOpts.onFirstDataRendered(params);
      params.api.applyColumnState({
        state: [{ colId: "trackedSearchTerms", sort: "desc" }],
        defaultState: { sort: null },
      });

      setTimeout(() => {
        if (params.api.getColumnState().find((c) => c.colId === "parentAsin")?.rowGroup === true) {
          this.isGroupedByParent.set(true);
        }
        if (this.asinInitValue) {
          params.api.setColumnFilterModel("asin", { type: "equals", filter: this.asinInitValue }).then(() => {
            params.api.onFilterChanged();
            // remove the asin from the url to avoid filtering again on refresh
            this.router.navigate([], {
              queryParams: { [ProductTrackerPageQueryParams.asin]: null },
              queryParamsHandling: "merge",
            });
          });
        }
      });
    },
    isExternalFilterPresent: (_) => this.isSegmentFilterPresent() || this.isCatalogFilterPresent(),
    doesExternalFilterPass: (node: IRowNode<ProductTrackerRow>) =>
      this.segmentFilterPass(node) && this.catalogFilterPass(node),
  };
  readonly gridData = signal<ProductTrackerRow[]>([]);

  categoryMap: Map<number, string> = new Map();

  ngOnInit(): void {
    this.authService.loggedUser$.pipe(untilDestroyed(this)).subscribe((u: User) => {
      this.locale = u.locale;
    });

    this.accountSelection.singleAccountMarketplaceSelection$
      .pipe(
        untilDestroyed(this),
        tap((am: AccountMarketplace) => {
          this.agGrid?.api.showLoadingOverlay();
          this.loadingService.loadingOn();
          this.accountMarketplace = am;
          this.currency = Marketplaces[am.marketplace].currency;
          const keywordTrackingSupported =
            keywordRankingAvailableFor(am.marketplace) && am.state == AccountState.BIDDER_ON;
          if (keywordTrackingSupported !== this.keywordTrackingSupported()) {
            this.keywordTrackingSupported.set(keywordTrackingSupported);
            this.agGrid?.api.setColumnsVisible(["trackedSearchTerms"], keywordTrackingSupported);
            this.agGrid?.api.setColumnsVisible(["reviews"], keywordTrackingSupported);
            this.agGrid?.api.setColumnsVisible(["reviewScore"], keywordTrackingSupported);
          }
        }),
        switchMap((am) => this.productTrackerService.productTrackerData$.pipe(map((data) => ({ am, data })))),
        switchMap(({ am, data }) => {
          const categoryIds = Array.from(
            new Set(
              data.data
                .map((row) => row.categoryId)
                .filter((id): id is string => id != null && id !== "0")
                .map((id) => +id),
            ),
          );

          return this.asinService.getCategoryNameMap(categoryIds, am.marketplace).pipe(
            map((categoryMap) => {
              this.categoryMap = categoryMap;
              return { am, data };
            }),
          );
        }),
      )
      .subscribe(({ am, data }) => {
        this.gridData.set(data.data);

        this.dataByParentAsin = data.parentData;
        this.loadingService.loadingOff();
      });

    this.accountSelection.singleAccountMarketplaceSelection$
      .pipe(
        untilDestroyed(this),
        switchMap((am: AccountMarketplace) =>
          this.keywordTrackingService.getProductTrackerConfig(am.accountId, am.marketplace),
        ),
      )
      .subscribe((s: ProductTrackerConfig[]) => (this.productTrackerConfig = s));

    combineLatest<[Map<number, SegmentEx>, ParamMap]>([
      this.accountSelection.singleAccountMarketplaceSelection$.pipe(
        switchMap((am) => this.segmentService.getSegments(am.accountId, am.marketplace)),
      ),
      this.route.queryParamMap,
    ])
      .pipe(untilDestroyed(this))
      .subscribe(([segmentIndex, params]) => {
        const segmentsInParamsIds = params.getAll(ProductTrackerPageQueryParams.segment);
        if (segmentsInParamsIds && segmentsInParamsIds.length > 0) {
          for (const id of segmentsInParamsIds) {
            const segment = segmentIndex.get(parseInt(id));
            if (segment && segment.segmentType === SegmentConfigType.ProductSegment)
              this.selectedSegments.update((s) => [...s, segment]);
          }
        }
        if (this.selectedSegments().length > 0) {
          this.segmentFilterChanged(this.selectedSegments());
        }
        this.asinInitValue = params.get(ProductTrackerPageQueryParams.asin)!;
      });
  }

  private displayGroupFormatter = (params: ICellRendererParams | ValueFormatterParams, isLink: boolean) => {
    const displayGroupValue =
      params.node?.field === "parentAsin" ? this.dataByParentAsin.get(params.node!.key!)?.displayGroup : params.value;

    const displayGroup: DisplayGroup | undefined = DisplayGroupsMap.get(
      displayGroupValue + "_" + this.accountMarketplace?.marketplace,
    );

    if (displayGroupValue && !displayGroup) return displayGroupValue; // display group exists as enum for not a formatted value
    if (!displayGroup) return undefined;

    return isLink
      ? `<a href='${displayGroup.displayGroupLink}' target="_blank" class="link">${displayGroup.displayGroupTitle}</a>`
      : displayGroup.displayGroupTitle;
  };

  private getProductMetricsDefs(): ColDef[] {
    const numericCol = { type: "numericColumn", filter: "agNumberColumnFilter" };

    const cols: ColDef[] = [];
    // skip "displayGroup" field (added manually)
    for (const [key, value] of Object.entries(SimpleProductMetrics)) {
      if (value.field === "displayGroup") continue;
      let col: ColDef = {
        headerName: this.translocoService.translate(value.title, {}, "en"),
        headerValueGetter: () => this.translocoService.translate(value.title),
        field: value.field,
        enableRowGroup: !value.numeric,
        filter: !value.numeric ? "agTextColumnFilter" : null,
        valueGetter: (params: ValueGetterParams<ProductTrackerRow>) => {
          if (params.node?.field === "parentAsin" && this.dataByParentAsin.get(params.node.key!))
            return this.dataByParentAsin.get(params.node.key!)![value.field as keyof ProductTrackerRow];
          if (value.field === "categoryId") {
            const raw = params.data![value.field];
            if (raw !== undefined) {
              const categoryId = +raw;
              if (this.categoryMap.has(categoryId)) return this.categoryMap.get(categoryId);
              else return undefined;
            }
            return params.data![value.field as keyof ProductTrackerRow];
          }
          if (value.field === "displayGroupRank" || value.field === "categoryRank") {
            return this.rankValueGetter(params, value.field);
          }
          if (!params.node?.group) return params.data![value.field as keyof ProductTrackerRow];
          return undefined;
        },
        valueFormatter: (params: ValueFormatterParams<ProductTrackerRow>) => {
          if (!params.value && (value.field === "displayGroupRank" || value.field === "categoryRank")) return "-";
          return params.value;
        },
        comparator: (valueA, valueB, nodeA, nodeB, isDescending) => {
          return this.productMetricComparator(valueA, valueB, value.field);
        },
      };
      if (value.numeric) col = { ...col, ...numericCol };
      cols.push(col);
    }
    return cols;
  }

  segmentFilterChanged(segments: SegmentEx[] | undefined) {
    this.selectedSegments.set(segments ?? []);
    this.agGrid?.api.onFilterChanged();
  }

  segmentFilterPass(node: IRowNode<ProductTrackerRow>): boolean {
    return (
      !this.selectedSegments() ||
      this.selectedSegments().length === 0 ||
      this.selectedSegments().some((s) => s.matchQuery(node.data!.asin!))
    );
  }

  isSegmentFilterPresent(): boolean {
    return this.selectedSegments().length > 0;
  }

  catalogFilterChanged(filter: Option<CatalogFilter> | undefined) {
    this.catalogFilterSelected.set(filter ?? this.catalogFilterDdOptions[0]);
    this.agGrid?.api.onFilterChanged();
  }

  catalogFilterPass(node: IRowNode<ProductTrackerRow>): boolean {
    const isCatalogProduct = node.data?.inCatalog;
    return (
      this.catalogFilterSelected().value === CatalogFilter.All ||
      (this.catalogFilterSelected().value === CatalogFilter.Catalog && isCatalogProduct) ||
      (this.catalogFilterSelected().value === CatalogFilter.NotInCatalog && !isCatalogProduct)
    );
  }

  isCatalogFilterPresent(): boolean {
    return this.catalogFilterSelected() && this.catalogFilterSelected().value !== CatalogFilter.All;
  }

  exportAsCsv() {
    const colsId = this.agGrid?.api
      ?.getAllDisplayedColumns()
      .map((c) => c.getColId())
      .filter((c) => c !== IMAGE_COL_ID && c !== ACTIONS_COL_ID);
    const fileName = getCsvFileName(
      "product_tracker",
      this.accountMarketplace?.accountGroupName,
      this.accountMarketplace?.marketplace,
    );
    exportGridCsv(this.agGrid?.api, { columnKeys: colsId, fileName: fileName });
  }

  setupGroupBy() {
    const colDefs: ColDef[] = this.agGrid.api.getColumnDefs() as ColDef[];
    colDefs.forEach((c) => {
      if (c.colId === "parentAsin") {
        c.rowGroup = !this.isGroupedByParent();
        c.hide = !this.isGroupedByParent();
      }
    });
    this.isGroupedByParent.update((grouped) => !grouped);
    this.agGrid.api.setGridOption("columnDefs", colDefs);
  }

  openAddProductTrackingModal() {
    this.keywordTrackerService.openAddProductTrackingModal(
      this.accountMarketplace!.accountId,
      this.accountMarketplace!.marketplace,
      this.productTrackerService.getCatalog(),
      this.productTrackerConfig,
    );
  }

  navigateToTrackedKeywordsDetails(product: string) {
    this.router.navigate([this.productTrackerUrl + "/", product]).then();
  }

  navigateToProductTimeline(product: string) {
    this.router.navigate([this.productTrackerUrl + "/product-timeline", product]).then();
  }

  private rankValueGetter(
    params: ValueGetterParams<ProductTrackerRow>,
    field: keyof ProductTrackerRow,
  ): string | number | boolean | string[] | undefined {
    const valueRank = params.data![field];
    if (valueRank !== undefined && valueRank !== 0) {
      return params.data![field];
    }
    return undefined;
  }

  private productMetricComparator(valueA: number | string, valueB: number | string, field: string): number {
    if ((field === "displayGroupRank" || field === "categoryRank") && !valueA) return 1;
    return valueA > valueB ? 1 : -1;
  }
}
